import type { FC } from "react";
import { memo, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";

import type { WorkspaceFormProps } from "./workspace-form.types";
import { FormWrapper, StyledTextField, palette } from "@src/core/styles";
import { FieldGroup } from "@src/pages/login-page/login-page.styles";
import {
    UpdateWorkspaceRequestParams,
    WorkspaceErrorResponse,
    WorkspaceErrors,
    WorkspaceFormValues,
} from "@src/core/types";
import { formSpaceSchema } from "@src/core/schemas";
import { Button, LoadFile } from "@src/components";
import { ReactComponent as Check } from "@src/core/icons/check.svg";
import { ReactComponent as DeleteIcon } from "@src/core/icons/delete.svg";
import { RoutePageTitle } from "../../form-components.styles";
import {
  ButtonGroup,
  ButtonGroupWrapper,
  DeleteIconWrapper,
} from "./workspace-form.styles";
import { FormContainer } from "../method-form/method-form.styles";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Workspace } from "@src/core/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { APP_ROUTES } from "@src/core/constants";
import { getKeyWordsForm } from "@src/core/utils";
import { useSnackbar } from "notistack";
import { DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
  StyledDialog,
  StyledDialogContentText,
} from "../../../route-component/components/workspace-menu/workspace-menu.styles";
import { AxiosError } from "axios";

export const WorkspaceForm: FC<WorkspaceFormProps> = memo(({ titlePage }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);
  const [fileId, setFileId] = useState();
  const [errorsRequest, setErrorsRequest] = useState<WorkspaceErrors>();

  const { formMethod } = getKeyWordsForm(pathname);

  const { data: workspace, isLoading } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => {
      return workspaceId
        ? Workspace.getOne({
            workspaceId,
          })
        : Promise.resolve(null);
    },
  });

  const { refetch } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => Workspace.getAll(),
    enabled: false,
  });

  const inititalValues: string | undefined = useMemo(() => {
    if (isLoading) {
      return undefined;
    }
    if (!isLoading) {
      if (workspace?.data && workspaceId) {
        return workspace?.data[0].name;
      } else {
        return undefined;
      }
    }
  }, [isLoading, workspace?.data, workspaceId]);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<WorkspaceFormValues>({
    mode: "onChange",
    resolver: joiResolver(formSpaceSchema),
    defaultValues: {
      name:
        formMethod === "update"
          ? inititalValues ?? (isLoading ? "" : "новое пространство")
          : "новое пространство",
    },
  });

  useEffect(() => {
    reset({ name: workspace?.data[0].name ?? undefined });
  }, [reset, workspace?.data]);

  const { mutate: createWorkspace } = useMutation(Workspace.create, {
    onSuccess: (response) => {
      navigate(`${APP_ROUTES.WORKSPACE}/${response.data.workspace.id}`);
      refetch();
      enqueueSnackbar("Пространство создано", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
    },
    onError: (error: AxiosError<WorkspaceErrorResponse>) => {
      setErrorsRequest(error.response?.data.errors);
    },
  });

  const { mutate: updateWorkspace } = useMutation(Workspace.update, {
    onSuccess: (response) => {
      refetch();
      enqueueSnackbar("Пространство обновлено", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
    },
    onError: (error: AxiosError<WorkspaceErrorResponse>) => {
      setErrorsRequest(error.response?.data.errors);
    },
  });

  const { mutate: deleteWorkspace } = useMutation(Workspace.delete, {
    onSuccess: (response) => {
      setOpenDialog(false);
      navigate(`${APP_ROUTES.WORKSPACE}`);
      enqueueSnackbar("Пространство удалено", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
      reset();
      refetch();
    },
    onError: (error: Error) => {
      // код в случае ошибки
    },
  });

  const cancelHandle = () => {
    navigate(APP_ROUTES.WORKSPACE);
  };

  const onSubmit = (data: WorkspaceFormValues) => {
    // вызов запросов на create or update space
    if (formMethod === "create") {
      const req = fileId ? { ...data, file_id: fileId } : data;
      // console.log({ req });
      createWorkspace(req);
      refetch();
    }
    if (formMethod !== "update") {
      return;
    }
    const req = fileId ? { ...data, workspaceId, file_id: fileId} : { ...data, workspaceId};
      if (req) updateWorkspace(req as UpdateWorkspaceRequestParams);
  };

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleExport = () => {
    if (!workspace?.data?.[0]) return;
    const exportData = {
      name: workspace.data[0].name,
      routes: workspace.data[0].routes?.map((route) => ({
        name: route.name,
        description: route.description,
        method: route.method,
        http_code: route.http_code,
        active: route.active,
      })) ?? [],
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workspace-${workspace.data[0].name}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const removeHandler = () => {
    setOpenDialog(false);
    if (workspaceId) deleteWorkspace({ workspaceId: workspaceId.toString() });
  };

  return (
    <FormContainer>
      <FormWrapper>
        <RoutePageTitle>{titlePage}</RoutePageTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FieldGroup>
            <Controller
              control={control}
              name="name"
              render={({ field: { value, onBlur, onChange } }) => (
                <StyledTextField
                  onBlur={onBlur}
                  variant="outlined"
                  label="Имя"
                  InputLabelProps={{
                    sx: {
                      "&.Mui-focused": {
                        color: palette.colors.green_base,
                      },
                      "&.Mui-error": { color: palette.colors.error },
                    },
                  }}
                  value={value}
                  error={!!errors?.name?.message || !!errorsRequest?.name}
                  helperText={errors?.name?.message || errorsRequest?.name}
                  onChange={onChange}
                />
              )}
            />
            <LoadFile setFileId={setFileId} />
          </FieldGroup>

          <ButtonGroupWrapper>
            <ButtonGroup>
              <Button
                text="Сохранить"
                type="submit"
                icon={<Check />}
                borderradius="L"
                variant="contained"
                disabled={!isValid}
              />
              <Button
                text="Отменить"
                variant="text"
                borderradius="XXXL"
                onClick={cancelHandle}
              />
            </ButtonGroup>
            {formMethod === "update" && (
              <>
                <Button
                  text="Экспорт JSON"
                  variant="text"
                  borderradius="XXXL"
                  onClick={handleExport}
                />
                <DeleteIconWrapper onClick={handleClickOpen}>
                  <DeleteIcon />
                </DeleteIconWrapper>
              </>
            )}
          </ButtonGroupWrapper>
        </form>
      </FormWrapper>

      <StyledDialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          backdropFilter: "blur(8px)",
        }}
      >
        <DialogTitle id="alert-dialog-title">Удаление</DialogTitle>
        <DialogContent>
          <StyledDialogContentText id="alert-dialog-description">
            Вы уверены, что хотите удалить данные
          </StyledDialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            text="Отмена"
            variant="text"
          />
          <Button
            onClick={removeHandler}
            text="Удалить"
            variant="filled"
            autoFocus
            borderradius="XXXL"
          >
            Agree
          </Button>
        </DialogActions>
      </StyledDialog>
    </FormContainer>
  );
});
